import { useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import Tab from 'react-bootstrap/Tab';
import Slider from 'react-slick';
import ApiService from '../helpers/api';
import { PlansResponse } from '../types/Interface/Plan';
import PlanDetails from './PlanDetails/Index';
import { Link } from 'react-router-dom';
import { SignUp_URL } from '../constants/Constants';
import { getMonthlyPrice } from '../helpers/Utils';

export default function Pricing() {
	// Define the pricing state as an object grouped by period
	const [plans, setPlans] = useState<PlansResponse[]>([]);
	const [tabKeys, setTabKeys] = useState<string[]>([]);
	const settings = {
		dots: true,
		infinite: true,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
		autoplay: false,
		autoplaySpeed: 3000,
		arrows: false,
	};
	const handleRedirect = (url: string) => {
		window.location.href = url;
	};

	const getPricing = async () => {
		try {
			const response = await ApiService.retrievePricing();
			if (response.isSuccess) {
				setPlans(response.response);
				// Extract unique periods for tabs
				const uniquePeriods = [...new Set(response.response.map((plan) => plan.period))];
				setTabKeys(uniquePeriods);
			} else {
				console.error('Error fetching pricing data:', response.error.message);
			}
		} catch (error) {
			console.error('Error fetching pricing data:', error);
		}
	};

	useEffect(() => {
		getPricing();
	}, []); // Empty dependency array means this runs once when the component mounts
	return (
		<>
			{plans && plans.length > 0 && (
				<div className="price-section blockElement" id="pricing-section">
					<div className="container">
						<div className="inner-price-section">
							<div className="bootstrap-tabing-pricing">
								<Tab.Container defaultActiveKey={tabKeys[0] || 'Monthly'} id="left-tabs-example">
									<Nav className="">
										{tabKeys.map((tabkey) => (
											<Nav.Item key={tabkey}>
												<Nav.Link eventKey={tabkey}>{tabkey === 'Yearly' ? 'Plans' : tabkey}</Nav.Link>
											</Nav.Item>
										))}
									</Nav>
									<Tab.Content className="tabs-content-main">
										{tabKeys.map((tabkey) => (
											<Tab.Pane eventKey={tabkey} key={tabkey}>
												<div className="tab-container-cstm">
													<Tab.Container defaultActiveKey={plans.filter((plan) => plan.period === tabkey).length > 1 ? 1 : 0} id="annually-tab">
														<Nav className="">
															{plans
																.filter((plan) => plan.period === tabkey)
																.map((plan, index) => (
																	<PlanDetails
																		tabkey={tabkey}
																		tabIndex={index}
																		eventKey={index?.toString()}
																		key={tabkey}
																		plan={plan}
																		handleRedirect={handleRedirect}
																	/>
																))}
														</Nav>
														<Tab.Content>
															{plans
																.filter((plan) => plan.period === tabkey)
																.map((plan, index) => {
																	// Create unique gradient IDs by including tabkey
																	const gradientId = `${tabkey}_${index}`;
																	return (
																		<Tab.Pane eventKey={index?.toString()} key={`${tabkey}_${index}`}>
																			<div className="tab-content-inner">
																				<h3>All plans include</h3>
																				<ul>
																					<li>
																						<svg width="30" height="29" viewBox="0 0 30 29" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M1.77441 19.8623C1.77441 16.0508 1.77441 14.1451 2.9585 12.961C4.14259 11.7769 6.04835 11.7769 9.85988 11.7769H20.6405C24.452 11.7769 26.3577 11.7769 27.5419 12.961C28.726 14.1451 28.726 16.0508 28.726 19.8623C28.726 23.6738 28.726 25.5796 27.5419 26.7637C26.3577 27.9478 24.452 27.9478 20.6405 27.9478H9.85988C6.04835 27.9478 4.14259 27.9478 2.9585 26.7637C1.77441 25.5796 1.77441 23.6738 1.77441 19.8623Z"
																								stroke={`url(#paint0_linear_${gradientId})`}
																								strokeWidth="2"
																							/>
																							<path
																								d="M7.16504 11.7767V9.08156C7.16504 4.61608 10.785 0.996094 15.2505 0.996094C19.716 0.996094 23.336 4.61608 23.336 9.08156V11.7767"
																								stroke={`url(#paint1_linear_${gradientId})`}
																								strokeWidth="2"
																								strokeLinecap="round"
																							/>
																							<path
																								d="M9.86035 19.8623H9.87248M15.2385 19.8623H15.2507M20.6288 19.8623H20.641"
																								stroke={`url(#paint2_linear_${gradientId})`}
																								strokeWidth="4"
																								strokeLinecap="round"
																								strokeLinejoin="round"
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="1.77441"
																									y1="19.8623"
																									x2="28.726"
																									y2="19.8623"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint1_linear_${gradientId}`}
																									x1="7.16504"
																									y1="6.38641"
																									x2="23.336"
																									y2="6.38641"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint2_linear_${gradientId}`}
																									x1="9.86035"
																									y1="20.3623"
																									x2="20.641"
																									y2="20.3623"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Secure Private Gallery
																					</li>
																					<li>
																						<svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								fillRule="evenodd"
																								clipRule="evenodd"
																								d="M19.1884 0.26418C19.6112 0.626495 19.6601 1.26286 19.2977 1.68556L16.116 5.39769H19.9503C21.7884 5.39766 23.2699 5.39764 24.4352 5.5543C25.645 5.71696 26.6635 6.06492 27.4725 6.87393C28.2816 7.68294 28.6296 8.70155 28.7922 9.91134C28.9488 11.0766 28.9488 12.5582 28.9488 14.3962V19.92C28.9488 21.7581 28.9488 23.2396 28.7922 24.4049C28.6296 25.6147 28.2816 26.6332 27.4726 27.4423C26.6635 28.2513 25.645 28.5993 24.4352 28.7619C23.2699 28.9186 21.7884 28.9185 19.9503 28.9185H9.05033C7.21223 28.9185 5.73066 28.9186 4.56541 28.7619C3.35562 28.5993 2.337 28.2513 1.52799 27.4423C0.718984 26.6332 0.371024 25.6147 0.208367 24.4049C0.0517048 23.2396 0.0517316 21.7581 0.0517585 19.92V14.3962C0.0517316 12.5581 0.0517048 11.0766 0.208367 9.91134C0.371024 8.70155 0.718984 7.68294 1.52799 6.87393C2.337 6.06493 3.35562 5.71696 4.56541 5.55431C5.73064 5.39765 7.21221 5.39766 9.05032 5.39769H12.8846L9.70278 1.68556C9.34046 1.26286 9.38941 0.626495 9.81211 0.26418C10.2348 -0.0981216 10.8712 -0.0491714 11.2335 0.373518L14.5003 4.18477L17.7671 0.373518C18.1293 -0.0491714 18.7657 -0.0981351 19.1884 0.26418ZM20.8845 7.41481V26.9014C22.2901 26.8971 23.3382 26.8752 24.1665 26.7639C25.1528 26.6312 25.675 26.3888 26.047 26.0167C26.419 25.6447 26.6615 25.1225 26.7941 24.1363C26.9306 23.121 26.9327 21.7754 26.9327 19.8462V14.47C26.9327 12.5408 26.9306 11.1952 26.7941 10.18C26.6615 9.19375 26.419 8.67148 26.047 8.2995C25.675 7.92753 25.1528 7.68499 24.1665 7.5524C23.3382 7.44105 22.2901 7.41911 20.8845 7.41481ZM18.8684 26.9024V7.41376H9.12408C7.19481 7.41376 5.84927 7.41591 4.83404 7.5524C3.84782 7.68499 3.32555 7.92753 2.95357 8.2995C2.58159 8.6715 2.33906 9.19375 2.20647 10.18C2.06997 11.1952 2.06783 12.5408 2.06783 14.47V19.8462C2.06783 21.7754 2.06997 23.121 2.20647 24.1363C2.33906 25.1225 2.58159 25.6447 2.95357 26.0167C3.32555 26.3888 3.84782 26.6312 4.83404 26.7639C5.84927 26.9003 7.19482 26.9024 9.12408 26.9024H18.8684Z"
																								fill={`url(#paint0_linear_mobile_${gradientId})`}
																							/>
																							<path
																								d="M25.2525 19.846C25.2525 19.1037 24.6508 18.502 23.9085 18.502C23.1662 18.502 22.5645 19.1037 22.5645 19.846C22.5645 20.5883 23.1662 21.19 23.9085 21.19C24.6508 21.19 25.2525 20.5883 25.2525 19.846Z"
																								fill={`url(#paint1_linear_mobile_${gradientId})`}
																							/>
																							<path
																								d="M25.2525 14.47C25.2525 13.7277 24.6508 13.126 23.9085 13.126C23.1662 13.126 22.5645 13.7277 22.5645 14.47C22.5645 15.2123 23.1662 15.8141 23.9085 15.8141C24.6508 15.8141 25.2525 15.2123 25.2525 14.47Z"
																								fill={`url(#paint2_linear_mobile_${gradientId})`}
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_mobile_${gradientId}`}
																									x1="0.0517578"
																									y1="14.47"
																									x2="28.9488"
																									y2="14.47"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint1_linear_mobile_${gradientId}`}
																									x1="22.5645"
																									y1="19.846"
																									x2="25.2525"
																									y2="19.846"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint2_linear_mobile_${gradientId}`}
																									x1="22.5645"
																									y1="14.47"
																									x2="25.2525"
																									y2="14.47"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Client Access on Mobile, Web and TVs
																					</li>
																					<li>
																						<svg width="31" height="30" viewBox="0 0 31 30" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M15.5003 1.58154V20.454M15.5003 20.454L21.3072 14.1027M15.5003 20.454L9.69336 14.1027"
																								stroke={`url(#paint0_linear_download_${gradientId})`}
																								strokeWidth="3"
																								strokeLinecap="round"
																								strokeLinejoin="round"
																							/>
																							<path
																								d="M19.8553 27.7128H11.1449C7.03883 27.7128 4.98578 27.7128 3.71017 26.4372C2.43457 25.1615 2.43457 23.1085 2.43457 19.0024M28.5657 19.0024C28.5657 23.1085 28.5657 25.1615 27.29 26.4372C26.8548 26.8724 26.3291 27.1591 25.6622 27.348"
																								stroke={`url(#paint1_linear_download_${gradientId})`}
																								strokeWidth="3"
																								strokeLinecap="round"
																								strokeLinejoin="round"
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_download_${gradientId}`}
																									x1="9.69336"
																									y1="11.0178"
																									x2="21.3072"
																									y2="11.0178"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint1_linear_download_${gradientId}`}
																									x1="2.43457"
																									y1="23.3576"
																									x2="28.5657"
																									y2="23.3576"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Download Options 4K, HD
																					</li>
																					<li>
																						<svg width="30" height="29" viewBox="0 0 30 29" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M18.9726 10.566H11.0264M14.9995 18.5122V10.566M22.9457 3.94414C22.9457 5.407 24.1316 6.59288 25.5944 6.59288M22.9457 3.94414C22.9457 2.48129 24.1316 1.29541 25.5944 1.29541C27.0573 1.29541 28.2432 2.48129 28.2432 3.94414C28.2432 5.407 27.0573 6.59288 25.5944 6.59288M22.9457 3.94414H7.05333M25.5944 6.59288L25.5944 22.4853M22.9457 25.134C22.9457 26.5969 24.1316 27.7827 25.5944 27.7827C27.0573 27.7827 28.2432 26.5969 28.2432 25.134C28.2432 23.6711 27.0573 22.4853 25.5944 22.4853M22.9457 25.134C22.9457 23.6711 24.1316 22.4853 25.5944 22.4853M22.9457 25.134L20.297 25.134M4.40459 6.59288C2.9417 6.59288 1.75586 5.407 1.75586 3.94414C1.75586 2.48129 2.9417 1.29541 4.40459 1.29541C5.86749 1.29541 7.05333 2.48129 7.05333 3.94414M4.40459 6.59288C5.86749 6.59288 7.05333 5.407 7.05333 3.94414M4.40459 6.59288L4.40458 9.24161M7.05333 25.134C7.05333 26.5969 5.86749 27.7827 4.40459 27.7827C2.9417 27.7827 1.75586 26.5969 1.75586 25.134C1.75586 23.6711 2.9417 22.4853 4.40459 22.4853M7.05333 25.134C7.05333 23.6711 5.86749 22.4853 4.40459 22.4853M7.05333 25.134L14.9995 25.134M4.40459 22.4853L4.40458 14.5391"
																								stroke={`url(#paint0_linear_${gradientId})`}
																								strokeWidth="2"
																								strokeLinecap="round"
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="1.75586"
																									y1="14.5391"
																									x2="28.2432"
																									y2="14.5391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Your Branding
																					</li>
																					<li>
																						<svg width="26" height="28" viewBox="0 0 26 28" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<mask id="path-1-inside-1_6780_3005" fill="white">
																								<path d="M21.1593 5.66317C20.7814 5.95092 20.3931 6.22494 19.9902 6.46975C20.1955 6.96452 20.3776 7.48678 20.5408 8.02621H18.8856C18.7911 7.74962 18.7009 7.46874 18.5935 7.20589C17.8265 7.55721 17.0268 7.82521 16.2064 8.02621H9.78901C8.95838 7.82435 8.14664 7.55034 7.37356 7.19129C7.26619 7.45757 7.17686 7.74447 7.07893 8.02621H5.4546C5.61695 7.48592 5.7982 6.96538 6.00521 6.46975C5.60063 6.22494 5.21151 5.95092 4.837 5.66317C4.11546 6.36581 3.48669 7.16036 2.96529 8.02621H0.656352C2.89313 3.45817 7.57714 0.29541 12.9981 0.29541C18.4209 0.29541 23.1032 3.45817 25.3391 8.02621H23.0319C22.5096 7.16036 21.8808 6.36409 21.1593 5.66317ZM6.03442 4.65301C6.24572 4.80419 6.45789 4.95279 6.67865 5.09023C6.95266 4.58601 7.25073 4.10756 7.57284 3.67635C7.02997 3.95981 6.51974 4.29052 6.03442 4.65301ZM8.02295 5.81177C8.717 6.13045 9.43167 6.38728 10.167 6.56767C10.3723 5.08335 10.6686 3.65058 11.0904 2.55452C9.90325 3.15495 8.85186 4.29395 8.02295 5.81177ZM13.1545 2.33892C13.1021 2.33806 13.0514 2.33205 12.9981 2.33205C12.9449 2.33205 12.8942 2.33806 12.8418 2.33892C12.4656 2.95481 12.0129 4.49238 11.6788 6.83052C12.116 6.88206 12.5558 6.9147 12.9973 6.9147C13.4414 6.9147 13.8803 6.88206 14.3158 6.83052C13.9834 4.49238 13.5307 2.95481 13.1545 2.33892ZM14.9145 2.57857C15.3311 3.67119 15.6249 5.0928 15.8293 6.56853C16.5535 6.39158 17.2561 6.13904 17.9364 5.82981C17.1109 4.32574 16.0742 3.18673 14.9145 2.57857ZM18.4252 3.67635C18.7464 4.10756 19.0428 4.58686 19.3202 5.09023C19.5401 4.95365 19.7523 4.80505 19.9636 4.65387C19.4774 4.29052 18.9681 3.95981 18.4252 3.67635Z" />
																								<path d="M4.837 22.4158C5.21323 22.1272 5.60493 21.854 6.00521 21.6075C5.80163 21.1145 5.61867 20.5922 5.4546 20.0519H7.07893C7.17256 20.3337 7.26447 20.6188 7.37184 20.886C8.14578 20.5269 8.95923 20.2555 9.78815 20.0519H16.2047C17.025 20.2529 17.8239 20.5218 18.591 20.8722C18.7 20.6085 18.7894 20.3268 18.8847 20.0519H20.5391C20.3768 20.5922 20.1947 21.1136 19.9894 21.6084C20.3931 21.8532 20.7805 22.1272 21.1584 22.4141C21.8791 21.7132 22.5079 20.9178 23.0284 20.0519H25.3365C23.1006 24.6182 18.4183 27.7827 12.9964 27.7827C7.57456 27.7827 2.89141 24.6182 0.655491 20.0519H2.96442C3.48668 20.9178 4.11546 21.7132 4.837 22.4158ZM19.9636 23.426C19.7523 23.2748 19.5393 23.1253 19.3202 22.9896C19.0428 23.493 18.7464 23.9714 18.4252 24.4026C18.968 24.1183 19.4774 23.7885 19.9636 23.426ZM17.9381 22.25C17.2578 21.9442 16.5552 21.6891 15.8311 21.513C15.6266 22.9896 15.3329 24.4061 14.9162 25.503C16.0742 24.8923 17.1109 23.7515 17.9381 22.25ZM12.8418 25.7392C12.8942 25.7409 12.9449 25.7461 12.9981 25.7461C13.0514 25.7461 13.1021 25.7409 13.1545 25.7392C13.5307 25.1233 13.9843 23.5849 14.3175 21.2476C13.882 21.1961 13.4422 21.1626 12.999 21.1626C12.5549 21.1626 12.116 21.1961 11.6796 21.2476C12.0155 23.5849 12.4656 25.1233 12.8418 25.7392ZM11.0904 25.5227C10.6686 24.4275 10.3723 22.9948 10.167 21.5096C9.43167 21.6908 8.717 21.9477 8.02295 22.2664C8.85186 23.785 9.90325 24.924 11.0904 25.5227ZM7.57284 24.4018C7.25073 23.9689 6.95266 23.4895 6.67865 22.9879C6.45789 23.1253 6.24572 23.2748 6.03441 23.426C6.51974 23.7885 7.02997 24.1183 7.57284 24.4018Z" />
																								<path d="M22.6247 16.7706L22.0775 14.8499C21.9324 14.338 21.8207 13.8338 21.7202 13.1517H21.6979C21.5862 13.8217 21.4857 14.3148 21.3517 14.8499L20.838 16.7706H19.2412L17.5662 11.3118H19.2292L19.6982 13.4773C19.821 14.1035 19.9327 14.7168 20.0108 15.3421H20.0332C20.1448 14.7159 20.29 14.1043 20.4352 13.4885L20.9824 11.3118H22.3224L22.903 13.5555C23.037 14.0906 23.1934 14.7177 23.305 15.3421H23.3274C23.4167 14.7159 23.5284 14.1129 23.64 13.5434L24.0755 11.3092H25.8072L24.2216 16.7698L22.6247 16.7706Z" />
																								<path d="M11.0122 13.4756C11.1367 14.1018 11.2458 14.7151 11.3231 15.3413H11.3463C11.4571 14.7151 11.6032 14.1026 11.7475 13.4867L12.2972 11.3101H13.6372L14.2179 13.5537C14.3519 14.0897 14.5082 14.7159 14.6199 15.3413H14.6422C14.7316 14.7151 14.8432 14.1112 14.9549 13.5417L15.3904 11.3075H17.1212L15.5356 16.768H13.9387L13.3916 14.8482C13.2464 14.3337 13.1347 13.8312 13.0342 13.1492H13.0119C12.9002 13.8192 12.7989 14.3122 12.6649 14.8482L12.1512 16.768H10.5544L8.88193 11.3075H10.5466L11.0122 13.4756Z" />
																								<path d="M2.32277 13.4756C2.44646 14.1018 2.55727 14.7151 2.63458 15.3413H2.65605C2.76686 14.7151 2.91374 14.1026 3.05891 13.4867L3.60608 11.3101H4.9478L5.52933 13.5537C5.66247 14.0897 5.81967 14.7159 5.93219 15.3413H5.95281C6.04214 14.7151 6.15295 14.1112 6.26462 13.5417L6.70098 11.3075H8.43354L6.84529 16.768H5.25102L4.70128 14.8482C4.55697 14.3337 4.44444 13.8312 4.34394 13.1492H4.32333C4.21252 13.8192 4.11202 14.3122 3.97802 14.8482L3.46435 16.768H1.86751L0.193359 11.3075H1.85806L2.32277 13.4756Z" />
																							</mask>
																							<path
																								d="M21.1593 5.66317C20.7814 5.95092 20.3931 6.22494 19.9902 6.46975C20.1955 6.96452 20.3776 7.48678 20.5408 8.02621H18.8856C18.7911 7.74962 18.7009 7.46874 18.5935 7.20589C17.8265 7.55721 17.0268 7.82521 16.2064 8.02621H9.78901C8.95838 7.82435 8.14664 7.55034 7.37356 7.19129C7.26619 7.45757 7.17686 7.74447 7.07893 8.02621H5.4546C5.61695 7.48592 5.7982 6.96538 6.00521 6.46975C5.60063 6.22494 5.21151 5.95092 4.837 5.66317C4.11546 6.36581 3.48669 7.16036 2.96529 8.02621H0.656352C2.89313 3.45817 7.57714 0.29541 12.9981 0.29541C18.4209 0.29541 23.1032 3.45817 25.3391 8.02621H23.0319C22.5096 7.16036 21.8808 6.36409 21.1593 5.66317ZM6.03442 4.65301C6.24572 4.80419 6.45789 4.95279 6.67865 5.09023C6.95266 4.58601 7.25073 4.10756 7.57284 3.67635C7.02997 3.95981 6.51974 4.29052 6.03442 4.65301ZM8.02295 5.81177C8.717 6.13045 9.43167 6.38728 10.167 6.56767C10.3723 5.08335 10.6686 3.65058 11.0904 2.55452C9.90325 3.15495 8.85186 4.29395 8.02295 5.81177ZM13.1545 2.33892C13.1021 2.33806 13.0514 2.33205 12.9981 2.33205C12.9449 2.33205 12.8942 2.33806 12.8418 2.33892C12.4656 2.95481 12.0129 4.49238 11.6788 6.83052C12.116 6.88206 12.5558 6.9147 12.9973 6.9147C13.4414 6.9147 13.8803 6.88206 14.3158 6.83052C13.9834 4.49238 13.5307 2.95481 13.1545 2.33892ZM14.9145 2.57857C15.3311 3.67119 15.6249 5.0928 15.8293 6.56853C16.5535 6.39158 17.2561 6.13904 17.9364 5.82981C17.1109 4.32574 16.0742 3.18673 14.9145 2.57857ZM18.4252 3.67635C18.7464 4.10756 19.0428 4.58686 19.3202 5.09023C19.5401 4.95365 19.7523 4.80505 19.9636 4.65387C19.4774 4.29052 18.9681 3.95981 18.4252 3.67635Z"
																								fill={`url(#paint0_linear_${gradientId})`}
																							/>
																							<path
																								d="M4.837 22.4158C5.21323 22.1272 5.60493 21.854 6.00521 21.6075C5.80163 21.1145 5.61867 20.5922 5.4546 20.0519H7.07893C7.17256 20.3337 7.26447 20.6188 7.37184 20.886C8.14578 20.5269 8.95923 20.2555 9.78815 20.0519H16.2047C17.025 20.2529 17.8239 20.5218 18.591 20.8722C18.7 20.6085 18.7894 20.3268 18.8847 20.0519H20.5391C20.3768 20.5922 20.1947 21.1136 19.9894 21.6084C20.3931 21.8532 20.7805 22.1272 21.1584 22.4141C21.8791 21.7132 22.5079 20.9178 23.0284 20.0519H25.3365C23.1006 24.6182 18.4183 27.7827 12.9964 27.7827C7.57456 27.7827 2.89141 24.6182 0.655491 20.0519H2.96442C3.48668 20.9178 4.11546 21.7132 4.837 22.4158ZM19.9636 23.426C19.7523 23.2748 19.5393 23.1253 19.3202 22.9896C19.0428 23.493 18.7464 23.9714 18.4252 24.4026C18.968 24.1183 19.4774 23.7885 19.9636 23.426ZM17.9381 22.25C17.2578 21.9442 16.5552 21.6891 15.8311 21.513C15.6266 22.9896 15.3329 24.4061 14.9162 25.503C16.0742 24.8923 17.1109 23.7515 17.9381 22.25ZM12.8418 25.7392C12.8942 25.7409 12.9449 25.7461 12.9981 25.7461C13.0514 25.7461 13.1021 25.7409 13.1545 25.7392C13.5307 25.1233 13.9843 23.5849 14.3175 21.2476C13.882 21.1961 13.4422 21.1626 12.999 21.1626C12.5549 21.1626 12.116 21.1961 11.6796 21.2476C12.0155 23.5849 12.4656 25.1233 12.8418 25.7392ZM11.0904 25.5227C10.6686 24.4275 10.3723 22.9948 10.167 21.5096C9.43167 21.6908 8.717 21.9477 8.02295 22.2664C8.85186 23.785 9.90325 24.924 11.0904 25.5227ZM7.57284 24.4018C7.25073 23.9689 6.95266 23.4895 6.67865 22.9879C6.45789 23.1253 6.24572 23.2748 6.03441 23.426C6.51974 23.7885 7.02997 24.1183 7.57284 24.4018Z"
																								fill={`url(#paint1_linear_${gradientId})`}
																							/>
																							<path
																								d="M22.6247 16.7706L22.0775 14.8499C21.9324 14.338 21.8207 13.8338 21.7202 13.1517H21.6979C21.5862 13.8217 21.4857 14.3148 21.3517 14.8499L20.838 16.7706H19.2412L17.5662 11.3118H19.2292L19.6982 13.4773C19.821 14.1035 19.9327 14.7168 20.0108 15.3421H20.0332C20.1448 14.7159 20.29 14.1043 20.4352 13.4885L20.9824 11.3118H22.3224L22.903 13.5555C23.037 14.0906 23.1934 14.7177 23.305 15.3421H23.3274C23.4167 14.7159 23.5284 14.1129 23.64 13.5434L24.0755 11.3092H25.8072L24.2216 16.7698L22.6247 16.7706Z"
																								fill={`url(#paint2_linear_${gradientId})`}
																							/>
																							<path
																								d="M11.0122 13.4756C11.1367 14.1018 11.2458 14.7151 11.3231 15.3413H11.3463C11.4571 14.7151 11.6032 14.1026 11.7475 13.4867L12.2972 11.3101H13.6372L14.2179 13.5537C14.3519 14.0897 14.5082 14.7159 14.6199 15.3413H14.6422C14.7316 14.7151 14.8432 14.1112 14.9549 13.5417L15.3904 11.3075H17.1212L15.5356 16.768H13.9387L13.3916 14.8482C13.2464 14.3337 13.1347 13.8312 13.0342 13.1492H13.0119C12.9002 13.8192 12.7989 14.3122 12.6649 14.8482L12.1512 16.768H10.5544L8.88193 11.3075H10.5466L11.0122 13.4756Z"
																								fill={`url(#paint3_linear_${gradientId})`}
																							/>
																							<path
																								d="M2.32277 13.4756C2.44646 14.1018 2.55727 14.7151 2.63458 15.3413H2.65605C2.76686 14.7151 2.91374 14.1026 3.05891 13.4867L3.60608 11.3101H4.9478L5.52933 13.5537C5.66247 14.0897 5.81967 14.7159 5.93219 15.3413H5.95281C6.04214 14.7151 6.15295 14.1112 6.26462 13.5417L6.70098 11.3075H8.43354L6.84529 16.768H5.25102L4.70128 14.8482C4.55697 14.3337 4.44444 13.8312 4.34394 13.1492H4.32333C4.21252 13.8192 4.11202 14.3122 3.97802 14.8482L3.46435 16.768H1.86751L0.193359 11.3075H1.85806L2.32277 13.4756Z"
																								fill={`url(#paint4_linear_${gradientId})`}
																							/>
																							<path
																								d="M21.1593 5.66317C20.7814 5.95092 20.3931 6.22494 19.9902 6.46975C20.1955 6.96452 20.3776 7.48678 20.5408 8.02621H18.8856C18.7911 7.74962 18.7009 7.46874 18.5935 7.20589C17.8265 7.55721 17.0268 7.82521 16.2064 8.02621H9.78901C8.95838 7.82435 8.14664 7.55034 7.37356 7.19129C7.26619 7.45757 7.17686 7.74447 7.07893 8.02621H5.4546C5.61695 7.48592 5.7982 6.96538 6.00521 6.46975C5.60063 6.22494 5.21151 5.95092 4.837 5.66317C4.11546 6.36581 3.48669 7.16036 2.96529 8.02621H0.656352C2.89313 3.45817 7.57714 0.29541 12.9981 0.29541C18.4209 0.29541 23.1032 3.45817 25.3391 8.02621H23.0319C22.5096 7.16036 21.8808 6.36409 21.1593 5.66317ZM6.03442 4.65301C6.24572 4.80419 6.45789 4.95279 6.67865 5.09023C6.95266 4.58601 7.25073 4.10756 7.57284 3.67635C7.02997 3.95981 6.51974 4.29052 6.03442 4.65301ZM8.02295 5.81177C8.717 6.13045 9.43167 6.38728 10.167 6.56767C10.3723 5.08335 10.6686 3.65058 11.0904 2.55452C9.90325 3.15495 8.85186 4.29395 8.02295 5.81177ZM13.1545 2.33892C13.1021 2.33806 13.0514 2.33205 12.9981 2.33205C12.9449 2.33205 12.8942 2.33806 12.8418 2.33892C12.4656 2.95481 12.0129 4.49238 11.6788 6.83052C12.116 6.88206 12.5558 6.9147 12.9973 6.9147C13.4414 6.9147 13.8803 6.88206 14.3158 6.83052C13.9834 4.49238 13.5307 2.95481 13.1545 2.33892ZM14.9145 2.57857C15.3311 3.67119 15.6249 5.0928 15.8293 6.56853C16.5535 6.39158 17.2561 6.13904 17.9364 5.82981C17.1109 4.32574 16.0742 3.18673 14.9145 2.57857ZM18.4252 3.67635C18.7464 4.10756 19.0428 4.58686 19.3202 5.09023C19.5401 4.95365 19.7523 4.80505 19.9636 4.65387C19.4774 4.29052 18.9681 3.95981 18.4252 3.67635Z"
																								stroke="#0D0D0D"
																								strokeWidth="0.2"
																								mask="url(#path-1-inside-1_67800_3005)"
																							/>
																							<path
																								d="M4.837 22.4158C5.21323 22.1272 5.60493 21.854 6.00521 21.6075C5.80163 21.1145 5.61867 20.5922 5.4546 20.0519H7.07893C7.17256 20.3337 7.26447 20.6188 7.37184 20.886C8.14578 20.5269 8.95923 20.2555 9.78815 20.0519H16.2047C17.025 20.2529 17.8239 20.5218 18.591 20.8722C18.7 20.6085 18.7894 20.3268 18.8847 20.0519H20.5391C20.3768 20.5922 20.1947 21.1136 19.9894 21.6084C20.3931 21.8532 20.7805 22.1272 21.1584 22.4141C21.8791 21.7132 22.5079 20.9178 23.0284 20.0519H25.3365C23.1006 24.6182 18.4183 27.7827 12.9964 27.7827C7.57456 27.7827 2.89141 24.6182 0.655491 20.0519H2.96442C3.48668 20.9178 4.11546 21.7132 4.837 22.4158ZM19.9636 23.426C19.7523 23.2748 19.5393 23.1253 19.3202 22.9896C19.0428 23.493 18.7464 23.9714 18.4252 24.4026C18.968 24.1183 19.4774 23.7885 19.9636 23.426ZM17.9381 22.25C17.2578 21.9442 16.5552 21.6891 15.8311 21.513C15.6266 22.9896 15.3329 24.4061 14.9162 25.503C16.0742 24.8923 17.1109 23.7515 17.9381 22.25ZM12.8418 25.7392C12.8942 25.7409 12.9449 25.7461 12.9981 25.7461C13.0514 25.7461 13.1021 25.7409 13.1545 25.7392C13.5307 25.1233 13.9843 23.5849 14.3175 21.2476C13.882 21.1961 13.4422 21.1626 12.999 21.1626C12.5549 21.1626 12.116 21.1961 11.6796 21.2476C12.0155 23.5849 12.4656 25.1233 12.8418 25.7392ZM11.0904 25.5227C10.6686 24.4275 10.3723 22.9948 10.167 21.5096C9.43167 21.6908 8.717 21.9477 8.02295 22.2664C8.85186 23.785 9.90325 24.924 11.0904 25.5227ZM7.57284 24.4018C7.25073 23.9689 6.95266 23.4895 6.67865 22.9879C6.45789 23.1253 6.24572 23.2748 6.03441 23.426C6.51974 23.7885 7.02997 24.1183 7.57284 24.4018Z"
																								stroke="#0D0D0D"
																								strokeWidth="0.2"
																								mask="url(#path-1-inside-1_67800_3005)"
																							/>
																							<path
																								d="M22.6247 16.7706L22.0775 14.8499C21.9324 14.338 21.8207 13.8338 21.7202 13.1517H21.6979C21.5862 13.8217 21.4857 14.3148 21.3517 14.8499L20.838 16.7706H19.2412L17.5662 11.3118H19.2292L19.6982 13.4773C19.821 14.1035 19.9327 14.7168 20.0108 15.3421H20.0332C20.1448 14.7159 20.29 14.1043 20.4352 13.4885L20.9824 11.3118H22.3224L22.903 13.5555C23.037 14.0906 23.1934 14.7177 23.305 15.3421H23.3274C23.4167 14.7159 23.5284 14.1129 23.64 13.5434L24.0755 11.3092H25.8072L24.2216 16.7698L22.6247 16.7706Z"
																								stroke="#0D0D0D"
																								strokeWidth="0.2"
																								mask="url(#path-1-inside-1_67800_3005)"
																							/>
																							<path
																								d="M11.0122 13.4756C11.1367 14.1018 11.2458 14.7151 11.3231 15.3413H11.3463C11.4571 14.7151 11.6032 14.1026 11.7475 13.4867L12.2972 11.3101H13.6372L14.2179 13.5537C14.3519 14.0897 14.5082 14.7159 14.6199 15.3413H14.6422C14.7316 14.7151 14.8432 14.1112 14.9549 13.5417L15.3904 11.3075H17.1212L15.5356 16.768H13.9387L13.3916 14.8482C13.2464 14.3337 13.1347 13.8312 13.0342 13.1492H13.0119C12.9002 13.8192 12.7989 14.3122 12.6649 14.8482L12.1512 16.768H10.5544L8.88193 11.3075H10.5466L11.0122 13.4756Z"
																								stroke="#0D0D0D"
																								strokeWidth="0.2"
																								mask="url(#path-1-inside-1_67800_3005)"
																							/>
																							<path
																								d="M2.32277 13.4756C2.44646 14.1018 2.55727 14.7151 2.63458 15.3413H2.65605C2.76686 14.7151 2.91374 14.1026 3.05891 13.4867L3.60608 11.3101H4.9478L5.52933 13.5537C5.66247 14.0897 5.81967 14.7159 5.93219 15.3413H5.95281C6.04214 14.7151 6.15295 14.1112 6.26462 13.5417L6.70098 11.3075H8.43354L6.84529 16.768H5.25102L4.70128 14.8482C4.55697 14.3337 4.44444 13.8312 4.34394 13.1492H4.32333C4.21252 13.8192 4.11202 14.3122 3.97802 14.8482L3.46435 16.768H1.86751L0.193359 11.3075H1.85806L2.32277 13.4756Z"
																								stroke="#0D0D0D"
																								strokeWidth="0.2"
																								mask="url(#path-1-inside-1_67800_3005)"
																							/>
																							<defs>
																								<linearGradient
																									id="paint0_linear_67800_3005"
																									x1="0.193359"
																									y1="14.0391"
																									x2="25.8072"
																									y2="14.0391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id="paint1_linear_67800_3005"
																									x1="0.193359"
																									y1="14.0391"
																									x2="25.8072"
																									y2="14.0391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id="paint2_linear_67800_3005"
																									x1="0.193359"
																									y1="14.0391"
																									x2="25.8072"
																									y2="14.0391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id="paint3_linear_67800_3005"
																									x1="0.193359"
																									y1="14.0391"
																									x2="25.8072"
																									y2="14.0391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id="paint4_linear_67800_3005"
																									x1="0.193359"
																									y1="14.0391"
																									x2="25.8072"
																									y2="14.0391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Domain Available
																						{/* <span className="coming-soon">coming soon...</span> */}
																					</li>
																					<li>
																						<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M21.6457 11.4903H15.2739M26.7432 13.7815C26.7432 10.4269 26.7432 8.74962 25.7625 7.65937C25.6724 7.55909 25.577 7.46364 25.4767 7.37344C24.3864 6.39287 22.7092 6.39287 19.3545 6.39287H18.8783C17.4081 6.39287 16.673 6.39287 15.988 6.19761C15.6117 6.09034 15.2489 5.9401 14.907 5.74986C14.2846 5.40354 13.7648 4.88374 12.7251 3.84414L12.0239 3.14292C11.6755 2.79448 11.5012 2.62025 11.3182 2.46848C10.5288 1.81413 9.56045 1.41302 8.53958 1.31756C8.30279 1.29541 8.05641 1.29541 7.56365 1.29541C6.43898 1.29541 5.87664 1.29541 5.40824 1.38379C3.34621 1.77284 1.73329 3.38576 1.34424 5.44779C1.25586 5.91619 1.25586 6.47853 1.25586 7.6032M26.7321 19.1365C26.6863 22.2964 26.452 24.0878 25.2501 25.2897C23.7572 26.7827 21.3541 26.7827 16.5482 26.7827H11.4508C6.64484 26.7827 4.24189 26.7827 2.74887 25.2897C1.25586 23.7967 1.25586 21.3937 1.25586 16.5878V12.7647"
																								stroke={`url(#paint0_linear_${gradientId})`}
																								strokeWidth="2"
																								strokeLinecap="round"
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="1.25586"
																									y1="14.0391"
																									x2="26.7432"
																									y2="14.0391"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Unlimited Projects (limited by quota)
																					</li>
																					<li>
																						<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M18.2502 21.7863C20.2827 21.7863 21.9303 20.1387 21.9303 18.1062C21.9303 16.0738 20.2827 14.4262 18.2502 14.4262C16.2178 14.4262 14.5702 16.0738 14.5702 18.1062C14.5702 20.1387 16.2178 21.7863 18.2502 21.7863Z"
																								fill={`url(#paint0_linear_${gradientId})`}
																							/>
																							<path
																								d="M7.21001 21.7863C9.24246 21.7863 10.8901 20.1387 10.8901 18.1062C10.8901 16.0738 9.24246 14.4262 7.21001 14.4262C5.17756 14.4262 3.52994 16.0738 3.52994 18.1062C3.52994 20.1387 5.17756 21.7863 7.21001 21.7863Z"
																								fill={`url(#paint1_linear_${gradientId})`}
																							/>
																							<path
																								d="M29.2905 21.7863C31.3229 21.7863 32.9706 20.1387 32.9706 18.1062C32.9706 16.0738 31.3229 14.4262 29.2905 14.4262C27.258 14.4262 25.6104 16.0738 25.6104 18.1062C25.6104 20.1387 27.258 21.7863 29.2905 21.7863Z"
																								fill={`url(#paint2_linear_${gradientId})`}
																							/>
																							<path
																								d="M18.2502 32.8265C20.2827 32.8265 21.9303 31.1789 21.9303 29.1465C21.9303 27.114 20.2827 25.4664 18.2502 25.4664C16.2178 25.4664 14.5702 27.114 14.5702 29.1465C14.5702 31.1789 16.2178 32.8265 18.2502 32.8265Z"
																								fill={`url(#paint3_linear_${gradientId})`}
																							/>
																							<path
																								d="M7.21001 32.8265C9.24246 32.8265 10.8901 31.1789 10.8901 29.1465C10.8901 27.114 9.24246 25.4664 7.21001 25.4664C5.17756 25.4664 3.52994 27.114 3.52994 29.1465C3.52994 31.1789 5.17756 32.8265 7.21001 32.8265Z"
																								fill={`url(#paint4_linear_${gradientId})`}
																							/>
																							<path
																								d="M29.2905 32.8265C31.3229 32.8265 32.9706 31.1789 32.9706 29.1465C32.9706 27.114 31.3229 25.4664 29.2905 25.4664C27.258 25.4664 25.6104 27.114 25.6104 29.1465C25.6104 31.1789 27.258 32.8265 29.2905 32.8265Z"
																								fill={`url(#paint5_linear_${gradientId})`}
																							/>
																							<path
																								d="M18.2502 10.7461C20.2827 10.7461 21.9303 9.09847 21.9303 7.06602C21.9303 5.03357 20.2827 3.38595 18.2502 3.38595C16.2178 3.38595 14.5702 5.03357 14.5702 7.06602C14.5702 9.09847 16.2178 10.7461 18.2502 10.7461Z"
																								fill={`url(#paint6_linear_${gradientId})`}
																							/>
																							<path
																								d="M7.21004 10.7461C9.24249 10.7461 10.8901 9.09848 10.8901 7.06603C10.8901 5.03358 9.24249 3.38595 7.21004 3.38595C5.17759 3.38595 3.52996 5.03358 3.52996 7.06603C3.52996 9.09848 5.17759 10.7461 7.21004 10.7461Z"
																								fill={`url(#paint7_linear_${gradientId})`}
																							/>
																							<path
																								d="M29.2905 10.7461C31.3229 10.7461 32.9706 9.09847 32.9706 7.06602C32.9706 5.03357 31.3229 3.38595 29.2905 3.38595C27.258 3.38595 25.6104 5.03357 25.6104 7.06602C25.6104 9.09847 27.258 10.7461 29.2905 10.7461Z"
																								fill={`url(#paint8_linear_${gradientId})`}
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint1_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint2_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint3_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint4_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint5_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint6_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint7_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint8_linear_${gradientId}`}
																									x1="0.585938"
																									y1="18.1063"
																									x2="35.9147"
																									y2="18.1063"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Interactive Menus
																					</li>
																					<li>
																						<svg width="31" height="29" viewBox="0 0 31 29" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M1.04883 20.5781L29.9518 7.88893M2.45874 25.8652C2.45874 26.2391 2.60728 26.5977 2.87169 26.8621C3.1361 27.1265 3.49471 27.2751 3.86864 27.2751H27.132C27.506 27.2751 27.8646 27.1265 28.129 26.8621C28.3934 26.5977 28.5419 26.2391 28.5419 25.8652V2.6018C28.5419 2.22787 28.3934 1.86925 28.129 1.60485C27.8646 1.34044 27.506 1.19189 27.132 1.19189H3.86864C3.49471 1.19189 3.1361 1.34044 2.87169 1.60485C2.60728 1.86925 2.45874 2.22787 2.45874 2.6018V25.8652Z"
																								stroke={`url(#paint0_linear_${gradientId})`}
																								strokeWidth="2"
																								strokeLinecap="round"
																								strokeLinejoin="round"
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="1.04883"
																									y1="14.2335"
																									x2="29.9518"
																									y2="14.2335"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Watermark Included
																						<span className="coming-soon">coming soon...</span>
																					</li>
																					<li>
																						<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M24.4096 21.823H23.9854L24.1909 22.1941L26.0788 25.6032L26.0795 25.6044C26.2231 25.8602 26.2593 26.1625 26.1804 26.445C26.1015 26.7276 25.9137 26.9673 25.6584 27.1116L25.6581 27.1117C25.4927 27.2055 25.306 27.2551 25.1159 27.2558C24.9196 27.2554 24.727 27.2029 24.5577 27.1037C24.3885 27.0044 24.2486 26.8621 24.1524 26.6911C24.1523 26.691 24.1522 26.6908 24.1521 26.6906L16.3973 12.7293L16.3974 12.7293L16.3955 12.7261C16.2485 12.4702 16.2091 12.1663 16.286 11.8814C16.363 11.5964 16.55 11.3537 16.8059 11.2067C17.0618 11.0596 17.3657 11.0202 17.6506 11.0972C17.9349 11.174 18.1772 11.3603 18.3244 11.6154L22.6833 19.4777L22.7547 19.6065H22.902H26.4741C26.768 19.6065 27.0499 19.7233 27.2577 19.9311C27.4655 20.1389 27.5823 20.4208 27.5823 20.7147C27.5823 21.0087 27.4655 21.2905 27.2577 21.4984C27.0499 21.7062 26.768 21.823 26.4741 21.823H24.4096ZM8.49044 19.2344L8.28224 19.6065H8.7086H15.6083C15.9023 19.6065 16.1841 19.7233 16.392 19.9311C16.5998 20.1389 16.7166 20.4208 16.7166 20.7147C16.7166 21.0087 16.5998 21.2905 16.392 21.4984C16.1841 21.7062 15.9023 21.823 15.6083 21.823H7.20098H7.05392L6.98247 21.9515L4.34812 26.6906C4.34803 26.6908 4.34793 26.6909 4.34784 26.6911C4.25163 26.8621 4.11176 27.0044 3.94251 27.1037C3.77321 27.2029 3.58061 27.2554 3.38438 27.2558C3.19427 27.2551 3.00754 27.2055 2.84214 27.1117L2.84188 27.1116C2.58651 26.9673 2.39878 26.7276 2.31984 26.445C2.2409 26.1625 2.27719 25.8602 2.42077 25.6044L2.42146 25.6032L4.30937 22.1941L4.51489 21.823H4.09067H2.02618C1.73227 21.823 1.45039 21.7062 1.24256 21.4984C1.03473 21.2905 0.917969 21.0087 0.917969 20.7147C0.917969 20.4208 1.03473 20.1389 1.24256 19.9311C1.45039 19.7233 1.73227 19.6065 2.02618 19.6065H5.59829H5.74529L5.81675 19.4781L12.9202 6.71084L12.9884 6.58832L12.9194 6.46626L10.5697 2.31012L10.5688 2.30859C10.496 2.18187 10.4488 2.04206 10.43 1.89713C10.4112 1.75219 10.4212 1.60497 10.4593 1.46388C10.4974 1.32279 10.5629 1.19059 10.6521 1.07482L10.4541 0.922233L10.6521 1.07481C10.7413 0.959046 10.8524 0.861978 10.9792 0.789156C11.1059 0.716333 11.2457 0.66918 11.3906 0.650392C11.5355 0.631602 11.6828 0.641545 11.8239 0.67965C11.9649 0.717755 12.0972 0.783277 12.2129 0.872475C12.3285 0.96149 12.4254 1.07235 12.4981 1.19873C12.4983 1.19899 12.4984 1.19925 12.4986 1.19951L14.0325 3.91435L14.2481 4.29602L14.4668 3.91609L16.0695 1.13175L16.0696 1.1316C16.2166 0.875696 16.4593 0.688696 16.7443 0.611739C17.0292 0.534782 17.3331 0.574173 17.589 0.721245C17.8449 0.868317 18.0319 1.11102 18.1089 1.39597C18.1858 1.68092 18.1464 1.98477 17.9993 2.24067L17.9979 2.24317L8.49044 19.2344Z"
																								fill={`url(#paint0_linear_${gradientId})`}
																								stroke="#0D0D0D"
																								strokeWidth="0.5"
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="0.667969"
																									y1="13.9145"
																									x2="27.8323"
																									y2="13.9145"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						OTT App with Streamefy branding, available on Amazon Fire TV, Android TV, Google TV, LG, and Samsung TV.
																					</li>
																					<li>
																						<svg width="24" height="27" viewBox="0 0 24 27" fill="none" xmlns="http://www.w3.org/2000/svg">
																							<path
																								d="M12.0005 26.5057C11.5647 26.5094 11.1364 26.3924 10.7631 26.1675L6.09763 23.439C4.35106 22.4622 2.89329 21.0417 1.87166 19.321C0.850028 17.6003 0.300735 15.6403 0.279297 13.6393V6.18385C0.279504 5.68145 0.433561 5.19116 0.720746 4.77893C1.00793 4.3667 1.41447 4.05231 1.88568 3.87804L11.155 0.473123C11.701 0.273282 12.3 0.273282 12.846 0.473123L22.1153 3.87804C22.5865 4.05231 22.9931 4.3667 23.2803 4.77893C23.5675 5.19116 23.7215 5.68145 23.7217 6.18385V13.6393C23.7003 15.6403 23.151 17.6003 22.1294 19.321C21.1077 21.0417 19.65 22.4622 17.9034 23.439L13.238 26.1675C12.8646 26.3924 12.4363 26.5094 12.0005 26.5057ZM12.0005 2.2486C11.9395 2.2375 11.877 2.2375 11.816 2.2486L2.54668 5.66888C2.44227 5.70755 2.35267 5.7781 2.2906 5.87052C2.22852 5.96295 2.1971 6.07257 2.20081 6.18385V13.6393C2.2231 15.3043 2.68476 16.9339 3.53909 18.3632C4.39343 19.7925 5.61012 20.9708 7.06607 21.7788L11.7315 24.5151C11.814 24.5601 11.9065 24.5837 12.0005 24.5837C12.0945 24.5837 12.187 24.5601 12.2695 24.5151L16.9349 21.7788C18.3909 20.9708 19.6076 19.7925 20.4619 18.3632C21.3162 16.9339 21.7779 15.3043 21.8002 13.6393V6.18385C21.8007 6.07517 21.7678 5.96896 21.7059 5.87961C21.6441 5.79025 21.5562 5.72206 21.4543 5.68426L12.185 2.27934C12.1259 2.25792 12.0634 2.2475 12.0005 2.2486Z"
																								fill={`url(#paint0_linear_${gradientId})`}
																							/>
																							<path
																								d="M11.6835 17.2136C11.4567 17.2121 11.2371 17.1335 11.0609 16.9907L7.7788 14.2236C7.5831 14.0595 7.46061 13.8244 7.43826 13.57C7.41592 13.3155 7.49556 13.0627 7.65966 12.867C7.82377 12.6713 8.05889 12.5488 8.31331 12.5264C8.56773 12.5041 8.82061 12.5837 9.01631 12.7478L11.5912 14.9154L15.9878 10.073C16.1591 9.88338 16.3986 9.76959 16.6537 9.75662C16.9089 9.74364 17.1587 9.83255 17.3483 10.0038C17.5379 10.175 17.6517 10.4146 17.6647 10.6697C17.6776 10.9248 17.5887 11.1747 17.4175 11.3643L12.4367 16.8984C12.3414 17.0022 12.2247 17.0842 12.0946 17.1386C11.9646 17.193 11.8243 17.2186 11.6835 17.2136Z"
																								fill={`url(#paint1_linear_${gradientId})`}
																							/>
																							<defs>
																								<linearGradient
																									id={`paint0_linear_${gradientId}`}
																									x1="0.279297"
																									y1="13.4145"
																									x2="23.7217"
																									y2="13.4145"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																								<linearGradient
																									id={`paint1_linear_${gradientId}`}
																									x1="7.43457"
																									y1="13.4848"
																									x2="17.6659"
																									y2="13.4848"
																									gradientUnits="userSpaceOnUse"
																								>
																									<stop stopColor="#941AF2" />
																									<stop offset="1" stopColor="#EFBD8A" />
																								</linearGradient>
																							</defs>
																						</svg>
																						Two-Factor Authentication
																					</li>
																					{/* <li>
																					<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg">
																						<path
																							d="M24.7131 14.714C24.7131 11.5144 23.3104 8.64249 21.0864 6.67955M3.28791 14.714C3.28791 11.5144 4.69062 8.64249 6.91463 6.67955M11.3224 25.0892C12.1783 25.3095 13.0758 25.4266 14.0005 25.4266C14.9253 25.4266 15.8227 25.3095 16.6787 25.0892M14.0005 9.3577C11.7818 9.3577 9.98331 7.55912 9.98331 5.34047C9.98331 3.12182 11.7818 1.32324 14.0005 1.32324C16.2193 1.32324 18.0178 3.12182 18.0178 5.34047C18.0178 7.55912 16.2193 9.3577 14.0005 9.3577ZM5.29653 25.4266C3.07788 25.4266 1.2793 23.6281 1.2793 21.4094C1.2793 19.1907 3.07788 17.3922 5.29653 17.3922C7.51517 17.3922 9.31375 19.1907 9.31375 21.4094C9.31375 23.6281 7.51517 25.4266 5.29653 25.4266ZM22.7045 25.4266C20.4858 25.4266 18.6873 23.6281 18.6873 21.4094C18.6873 19.1907 20.4858 17.3922 22.7045 17.3922C24.9233 17.3922 26.7218 19.1907 26.7218 21.4094C26.7218 23.6281 24.9233 25.4266 22.7045 25.4266Z"
																							stroke="url(#paint0_linear_6780_20993)"
																							strokeWidth="2"
																							strokeLinecap="round"
																						/>
																						<defs>
																							<linearGradient
																								id="paint0_linear_6780_20993"
																								x1="1.2793"
																								y1="13.3749"
																								x2="26.7218"
																								y2="13.3749"
																								gradientUnits="userSpaceOnUse"
																							>
																								<stop stopColor="#941AF2" />
																								<stop offset="1" stopColor="#EFBD8A" />
																							</linearGradient>
																						</defs>
																					</svg>
																					White Label App
																				</li> */}
																				</ul>
																			</div>
																		</Tab.Pane>
																	);
																})}
														</Tab.Content>
													</Tab.Container>
												</div>
												<div className="slide-container-cstm">
													<Slider {...settings}>
														{plans
															.filter((plan) => plan.period === tabkey)
															.map((plan, index) => (
																<div className="single-slide" key={`${tabkey}_slide_${index}`}>
																	<div className="box-pricing-single">
																		<h6>{plan.name}</h6>
																		{plan.usdPrice && (
																			<div className="price-about-pricing">
																				<h4>
																					<svg xmlns="http://www.w3.org/2000/svg" fill="#fff" width="40px" height="40px" viewBox="0 0 1024 1024">
																						<path d="M541.7 768v-45.3c46.3-2.4 81.5-15 108.7-37.8 27.2-22.8 40.8-53.1 40.8-88.2 0-37.8-11-65.7-35.3-83.4-24.6-20.1-59.8-35.4-111.6-45.3h-2.6V351.8c35.3 5.1 65.3 15 95.1 35.4l43.6-55.5c-43.6-27.9-89.9-42.9-138.8-45.3V256h-40.8v30.3c-40.8 2.4-76.3 15-103.5 37.8-27.2 22.8-40.8 53.1-40.8 88.2s11 63 35.3 80.7c21.7 17.7 59.8 32.7 108.7 42.9v118.5c-38.2-5.1-76.3-22.8-114.2-53.1l-48.9 53.1c48.9 40.5 103.5 63 163.3 68.1V768h41zm2.6-219.6c27.2 7.5 43.6 15 54.4 22.8 8.1 10.2 13.6 20.1 13.6 35.4s-5.5 25.2-19.1 35.4c-13.6 10.2-30.1 15-48.9 17.7V548.4zM449.2 440c-8.1-7.5-13.6-20.1-13.6-32.7 0-15 5.5-25.2 16.2-35.4 13.6-10.2 27.2-15 48.9-17.7v108.6c-27.2-7.8-43.4-15.3-51.5-22.8z" />
																					</svg>
																					{plan.usdPrice}
																				</h4>
																			</div>
																		)}

																		<div className="btns-and-list-tabs">
																			<ul>
																				{plan.name === 'On Demand' ? (
																					<>
																						<li>
																							<label>
																								<svg fill="none" height="13" viewBox="0 0 16 13" width="16" xmlns="http://www.w3.org/2000/svg">
																									<path
																										d="M1 6.72494L5.66667 11.3916L15 1.3916"
																										// fill="white"
																										stroke="#EFBD8A"
																										strokeLinecap="round"
																										strokeLinejoin="round"
																										strokeWidth="2"
																									/>
																								</svg>
																								Scale instantly anytime
																							</label>
																						</li>
																						<small className="month-approx">(Flexible pricing for growing storage needs.)</small>
																					</>
																				) : (
																					<>
																						<li>
																							<label>
																								<svg fill="none" height="13" viewBox="0 0 16 13" width="16" xmlns="http://www.w3.org/2000/svg">
																									<path
																										d="M1 6.72494L5.66667 11.3916L15 1.3916"
																										// fill="white"
																										stroke="#EFBD8A"
																										strokeLinecap="round"
																										strokeLinejoin="round"
																										strokeWidth="2"
																									/>
																								</svg>
																								Upload Quota
																							</label>
																							<span>{plan.data}</span>
																						</li>
																						<small className="month-approx">(Approx. ${getMonthlyPrice(plan.usdPrice, plan.data)}/GB)</small>
																					</>
																				)}
																			</ul>
																			<Link
																				to=""
																				onClick={(e) => {
																					e.preventDefault();
																					handleRedirect(SignUp_URL);
																				}}
																				className="cmn-btn btn-price-area"
																			>
																				<span>
																					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
																						<path
																							d="M8.2821 16L15.7819 8.52686C15.9216 8.38684 16 8.19743 16 8C16 7.80257 15.9216 7.61316 15.7819 7.47314L8.2821 -3.3736e-07L7.22462 1.05371L13.442 7.24895L-3.16862e-07 7.24895L-3.82194e-07 8.74358L13.442 8.74358L7.22462 14.9388L8.2821 16Z"
																							fill="white"
																						></path>
																					</svg>
																					start free trial
																				</span>
																			</Link>
																		</div>
																		<div className="plan-details-pricing">
																			<h5>All Plan Include</h5>
																			<ul>
																				<li>Secure Private Gallery</li>
																				<li>Client Access on Mobile, Web and TVs</li>
																				<li>Download Options 4K, HD</li>
																				<li>Your Branding</li>
																				<li>Domain Available</li>
																				<li>Unlimited Projects (limited by quota)</li>
																				<li>Interactive Menus</li>
																				<li>
																					Watermark Included <span className="coming-soon">coming soon..</span>
																				</li>
																				<li>
																					OTT App with Streamefy branding, available on Amazon Fire TV, Android TV, Google TV, LG, and Samsung TV.
																				</li>
																				<li>Two-Factor Authentication</li>
																				{/* <li>White Label App</li> */}
																			</ul>
																		</div>
																	</div>
																</div>
															))}
													</Slider>
												</div>
											</Tab.Pane>
										))}
									</Tab.Content>
								</Tab.Container>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
